import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ApiRequestError } from '@concours/api-client'
import { Link, useParams } from 'react-router'
import { ArrowLeft, Mail, Plus, ScrollText, ShieldOff, UserCheck, UserX } from 'lucide-react'
import {
  Alert,
  Badge,
  Button,
  Can,
  Card,
  CardBody,
  CardHeader,
  ConfirmModal,
  Empty,
  InfoGrid,
  InfoItem,
  Input,
  Modal,
  PageHeader,
  Select,
  Table,
  Textarea,
  useAuth,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import {
  formatDate,
  fullName,
  SCOPE_LABELS,
  USER_STATUS,
  type AdminUserDetail,
  type Assignment,
  type PermissionModule,
  type ScopeType,
} from '../../lib/rbac-types'
import { useRoles } from './RolesPage'
import { USERS_KEY } from './UsersPage'
import { useSelection } from '../../lib/selection'
import type { Competition } from '../../lib/competition-types'
import type { Paginated } from '../../lib/rbac-types'

type Action = 'disable' | 'enable' | 'resetMfa' | 'resend'

/** Fiche d'un compte interne : profils attribués, historique, permissions effectives. */
export function UserDetailPage() {
  const { t } = useTranslation()
  const { id = '' } = useParams()
  const { session, user: me } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [action, setAction] = useState<Action | null>(null)
  const [assigning, setAssigning] = useState(false)
  const [revoking, setRevoking] = useState<Assignment | null>(null)
  const [showHistory, setShowHistory] = useState(false)

  const key = [...USERS_KEY, id]
  const detail = useQuery({
    queryKey: key,
    queryFn: () => session.api.get<AdminUserDetail>(`/admin/users/${id}`),
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: USERS_KEY })

  const runAction = useMutation({
    mutationFn: ({ kind, reason }: { kind: Action; reason?: string }) => {
      const path = {
        disable: 'disable',
        enable: 'enable',
        resetMfa: 'reset-mfa',
        resend: 'resend-invitation',
      }[kind]
      return session.api.post(`/admin/users/${id}/${path}`, reason ? { reason } : {})
    },
    onSuccess: async () => {
      setAction(null)
      toast.success(t('rbac.user.done'))
      await refresh()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const revoke = useMutation({
    mutationFn: ({ assignment, reason }: { assignment: Assignment; reason: string }) =>
      session.api.delete(`/admin/users/${id}/roles/${assignment.id}`, { body: { reason } }),
    onSuccess: async () => {
      setRevoking(null)
      toast.success(t('rbac.user.done'))
      await refresh()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  if (detail.isError)
    return (
      <>
        <BackLink />
        <Alert tone="danger">{errorMessage(detail.error)}</Alert>
      </>
    )
  const user = detail.data
  if (!user) return <BackLink />

  const isSelf = me?.id === user.id
  const active = user.assignments.filter((a) => !a.revokedAt)
  const history = user.assignments.filter((a) => a.revokedAt)

  const columns: Column<Assignment>[] = [
    {
      key: 'role',
      header: t('rbac.user.roleField'),
      cell: (a) => (
        <span>
          <Link to={`/profils/${a.role.id}`}>{a.role.label}</Link>{' '}
          {!a.role.isActive && <Badge variant="neutral">{t('rbac.roles.inactive')}</Badge>}
          {a.revokedAt && <Badge variant="neutral">{t('rbac.user.revoked')}</Badge>}
        </span>
      ),
    },
    {
      key: 'scope',
      header: t('rbac.editor.scope'),
      cell: (a) => (
        <span className="stack" style={{ gap: 2 }}>
          <span>{SCOPE_LABELS[a.scopeType]}</span>
          {a.scopeLabel && <span className="muted">{a.scopeLabel}</span>}
        </span>
      ),
    },
    {
      key: 'validity',
      header: t('rbac.editor.until'),
      cell: (a) => formatDate(a.validUntil),
    },
    {
      key: 'granted',
      header: t('rbac.user.grantedBy'),
      cell: (a) => (
        <div style={{ fontSize: 'var(--text-sm)' }}>
          <div>{a.grantedBy ?? '—'}</div>
          <span className="text-muted">{formatDate(a.grantedAt)}</span>
        </div>
      ),
    },
    {
      key: 'reason',
      header: t('rbac.user.reason'),
      cell: (a) => (
        <span style={{ fontSize: 'var(--text-sm)' }}>
          {a.revokedAt
            ? `${a.revokeReason ?? ''} (${a.revokedBy ?? '—'}, ${formatDate(a.revokedAt)})`
            : a.grantReason}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'end',
      cell: (a) =>
        a.revokedAt || isSelf ? null : (
          <Can perm="users.assign_roles">
            <Button variant="ghost" size="sm" onClick={() => setRevoking(a)}>
              {t('rbac.user.revoke')}
            </Button>
          </Can>
        ),
    },
  ]

  const confirmCopy: Record<
    Action,
    { title: string; text?: string; tone: 'primary' | 'danger'; reason: boolean }
  > = {
    disable: {
      title: t('rbac.user.disableTitle'),
      text: t('rbac.user.disableText'),
      tone: 'danger',
      reason: true,
    },
    enable: { title: t('rbac.user.enableTitle'), tone: 'primary', reason: true },
    resetMfa: {
      title: t('rbac.user.resetMfaTitle'),
      text: t('rbac.user.resetMfaText'),
      tone: 'danger',
      reason: true,
    },
    resend: {
      title: t('rbac.user.resend'),
      text: t('rbac.users.inviteText'),
      tone: 'primary',
      reason: false,
    },
  }

  return (
    <>
      <BackLink />
      <PageHeader
        eyebrow={t('rbac.users.title')}
        title={fullName(user)}
        description={user.email}
        actions={
          !isSelf && (
            <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              {user.status === 'pending' && (
                <Can perm="users.create">
                  <Button variant="secondary" icon={<Mail />} onClick={() => setAction('resend')}>
                    {t('rbac.user.resend')}
                  </Button>
                </Can>
              )}
              {user.mfaEnabled && (
                <Can perm="users.reset_mfa">
                  <Button
                    variant="secondary"
                    icon={<ShieldOff />}
                    onClick={() => setAction('resetMfa')}
                  >
                    {t('rbac.user.resetMfa')}
                  </Button>
                </Can>
              )}
              <Can perm="users.disable">
                {user.status === 'disabled' ? (
                  <Button
                    variant="secondary"
                    icon={<UserCheck />}
                    onClick={() => setAction('enable')}
                  >
                    {t('rbac.user.enable')}
                  </Button>
                ) : (
                  <Button variant="danger" icon={<UserX />} onClick={() => setAction('disable')}>
                    {t('rbac.user.disable')}
                  </Button>
                )}
              </Can>
            </span>
          )
        }
      />

      <div className="stack" style={{ gap: 'var(--space-6)' }}>
        {isSelf && <Alert tone="info">{t('rbac.user.self')}</Alert>}

        <Card>
          <CardHeader title={t('rbac.user.identity')} />
          <CardBody>
            <InfoGrid>
              <InfoItem label={t('rbac.users.status')}>
                <Badge variant={USER_STATUS[user.status].variant}>
                  {USER_STATUS[user.status].label}
                </Badge>
              </InfoItem>
              <InfoItem label={t('rbac.users.phone')}>{user.phone ?? '—'}</InfoItem>
              <InfoItem label={t('rbac.users.mfa')}>
                {user.mfaEnabled ? <Badge variant="success">MFA</Badge> : '—'}
              </InfoItem>
              <InfoItem label={t('rbac.users.lastLogin')}>{formatDate(user.lastLoginAt)}</InfoItem>
            </InfoGrid>
            {user.invitation && !user.invitation.acceptedAt && (
              <p className="text-muted" style={{ marginBlockStart: 'var(--space-3)' }}>
                {t('rbac.user.pendingInvitation', { date: formatDate(user.invitation.expiresAt) })}
              </p>
            )}
            {user.effective.requiresMfa && !user.mfaEnabled && user.status === 'active' && (
              <Alert tone="warning" className="mt-3">
                {t('rbac.mfaGate.title')}
              </Alert>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('rbac.user.assignments')}
            actions={
              <>
                {history.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setShowHistory((v) => !v)}>
                    {showHistory ? t('rbac.user.hideHistory') : t('rbac.user.history')}
                  </Button>
                )}
                {!isSelf && user.status !== 'disabled' && (
                  <Can perm="users.assign_roles">
                    <Button
                      variant="primary"
                      size="sm"
                      icon={<Plus />}
                      onClick={() => setAssigning(true)}
                    >
                      {t('rbac.user.assign')}
                    </Button>
                  </Can>
                )}
              </>
            }
          />
          <Table
            columns={columns}
            rows={showHistory ? [...active, ...history] : active}
            rowKey={(a) => String(a.id)}
            caption={t('rbac.user.assignments')}
            empty={<Empty title={t('rbac.users.noRole')} />}
          />
        </Card>

        <EffectivePermissions effective={user.effective} />

        <Can perm="audit.view">
          <div>
            <Link
              className="btn btn-secondary"
              to={`/audit?ressource=user&id=${encodeURIComponent(user.id)}`}
            >
              <ScrollText aria-hidden="true" /> {t('audit.journal')}
            </Link>
          </div>
        </Can>
      </div>

      {action && (
        <ConfirmModal
          open
          onClose={() => setAction(null)}
          title={confirmCopy[action].title}
          message={confirmCopy[action].text}
          tone={confirmCopy[action].tone}
          reasonRequired={confirmCopy[action].reason}
          onConfirm={async (reason) => {
            await runAction.mutateAsync({ kind: action, reason }).catch(() => undefined)
          }}
        />
      )}
      <ConfirmModal
        open={revoking !== null}
        onClose={() => setRevoking(null)}
        title={t('rbac.user.revokeTitle')}
        message={revoking ? `${revoking.role.label} — ${t('rbac.user.revokeText')}` : undefined}
        tone="danger"
        reasonRequired
        confirmLabel={t('rbac.user.revoke')}
        onConfirm={async (reason) => {
          if (revoking)
            await revoke
              .mutateAsync({ assignment: revoking, reason: reason ?? '' })
              .catch(() => undefined)
        }}
      />
      <AssignRoleModal
        open={assigning}
        onClose={() => setAssigning(false)}
        userId={user.id}
        onDone={refresh}
      />
    </>
  )
}

function BackLink() {
  const { t } = useTranslation()
  return (
    <Link
      to="/utilisateurs"
      className="btn btn-ghost btn-sm"
      style={{ marginBlockEnd: 'var(--space-3)' }}
    >
      <ArrowLeft aria-hidden="true" /> {t('rbac.user.back')}
    </Link>
  )
}

/** Résultat consolidé, groupé par module (libellés du catalogue). */
function EffectivePermissions({ effective }: { effective: AdminUserDetail['effective'] }) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const catalog = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: () => session.api.get<{ modules: PermissionModule[] }>('/admin/permissions'),
    staleTime: 5 * 60_000,
    retry: false,
  })
  const held = useMemo(() => new Set(effective.global), [effective.global])
  const scopedCount = Object.values(effective.scoped).reduce(
    (sum, ids) => sum + Object.keys(ids ?? {}).length,
    0
  )

  return (
    <Card>
      <CardHeader title={t('rbac.user.effective')} />
      <CardBody>
        <p className="text-muted" style={{ marginBlockEnd: 'var(--space-3)' }}>
          {t('rbac.user.effectiveHelp')}
        </p>
        {held.size === 0 && scopedCount === 0 ? (
          <Empty title={t('rbac.user.noPermission')} />
        ) : catalog.data ? (
          <div className="stack" style={{ gap: 'var(--space-3)' }}>
            {catalog.data.modules
              .map((module) => ({
                module,
                codes: module.permissions.filter((p) => held.has(p.code)),
              }))
              .filter((entry) => entry.codes.length)
              .map(({ module, codes }) => (
                <div key={module.code}>
                  <strong>{module.label}</strong>
                  <div
                    className="row"
                    style={{
                      gap: 'var(--space-1)',
                      flexWrap: 'wrap',
                      marginBlockStart: 'var(--space-1)',
                    }}
                  >
                    {codes.map((p) => (
                      <Badge
                        key={p.code}
                        variant={p.sensitive ? 'danger' : 'outline'}
                        title={p.code}
                      >
                        {p.label}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="row" style={{ gap: 'var(--space-1)', flexWrap: 'wrap' }}>
            {[...held].map((code) => (
              <Badge key={code} variant="outline" className="mono">
                {code}
              </Badge>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

function AssignRoleModal({
  open,
  onClose,
  userId,
  onDone,
}: {
  open: boolean
  onClose: () => void
  userId: string
  onDone: () => Promise<unknown>
}) {
  const { t } = useTranslation()
  const { session } = useAuth()
  const toast = useToast()
  const roles = useRoles()
  const empty = {
    roleId: '',
    scopeType: 'global' as ScopeType,
    competitionId: '',
    stageId: '',
    validFrom: '',
    validUntil: '',
    reason: '',
  }
  const [form, setForm] = useState(empty)
  const [force, setForce] = useState(false)
  const competitions = useQuery({
    queryKey: ['admin', 'competitions', 'scope-picker'],
    queryFn: () =>
      session.api
        .get<Paginated<Competition>>('/admin/competitions', { query: { perPage: 100 } })
        .then((r) => r.data),
    enabled: open && form.scopeType !== 'global',
  })
  const stages = useSelection(form.competitionId, open && form.scopeType === 'stage')
  const scopeId =
    form.scopeType === 'competition'
      ? form.competitionId
      : form.scopeType === 'stage'
        ? form.stageId
        : ''

  const assign = useMutation({
    mutationFn: () =>
      session.api.post<{ id: number; warnings: { message: string }[] }>(
        `/admin/users/${userId}/roles`,
        {
          roleId: form.roleId,
          scopeType: form.scopeType,
          scopeId: scopeId || null,
          validFrom: form.validFrom ? new Date(form.validFrom).toISOString() : null,
          validUntil: form.validUntil ? new Date(form.validUntil).toISOString() : null,
          reason: form.reason,
          ...(force ? { force: true } : {}),
        }
      ),
    onSuccess: async (result) => {
      if (result.warnings.length)
        toast.show({
          tone: 'warning',
          title: t('rbac.user.warning'),
          description: result.warnings.map((w) => w.message).join(' '),
          duration: 10_000,
        })
      else toast.success(t('rbac.user.assigned'))
      setForm(empty)
      setForce(false)
      onClose()
      await onDone()
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(assign.error)
  const options = (roles.data ?? []).filter((role) => role.isActive)
  // Cumul interdit (séparation des responsabilités) : un super-administrateur peut le forcer.
  const sod =
    assign.error instanceof ApiRequestError && assign.error.code === 'E_SOD_CONFLICT'
      ? (assign.error.details as {
          rules?: { code: string; message: string }[]
          forceable?: boolean
        } | null)
      : null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    assign.mutate()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('rbac.user.assignTitle')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('rbac.create.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="assign-role"
            loading={assign.isPending}
            disabled={
              !form.roleId ||
              form.reason.trim().length < 10 ||
              (form.scopeType !== 'global' && !scopeId)
            }
          >
            {t('rbac.user.assignSubmit')}
          </Button>
        </>
      }
    >
      <form id="assign-role" className="stack" onSubmit={submit}>
        <Select
          label={t('rbac.user.roleField')}
          required
          placeholder="—"
          value={form.roleId}
          error={errors.roleId}
          onChange={(e) => setForm({ ...form, roleId: e.target.value })}
          options={options.map((role) => ({
            value: role.id,
            label: role.requiresMfa ? `${role.label} (MFA)` : role.label,
          }))}
        />
        <Select
          label={t('rbac.user.scopeField')}
          value={form.scopeType}
          hint={t('rbac.user.scopeHint')}
          onChange={(e) =>
            setForm({
              ...form,
              scopeType: e.target.value as ScopeType,
              competitionId: '',
              stageId: '',
            })
          }
          options={(Object.keys(SCOPE_LABELS) as ScopeType[]).map((value) => ({
            value,
            label: SCOPE_LABELS[value],
          }))}
        />
        {form.scopeType !== 'global' && (
          <Select
            label={t('rbac.user.scopeCompetition')}
            required
            placeholder="—"
            value={form.competitionId}
            error={errors.scopeId}
            onChange={(e) => setForm({ ...form, competitionId: e.target.value, stageId: '' })}
            options={(competitions.data ?? []).map((c) => ({
              value: c.id,
              label: `${c.reference} — ${c.title}`,
            }))}
          />
        )}
        {form.scopeType === 'stage' && form.competitionId && (
          <Select
            label={t('rbac.user.scopeStage')}
            required
            placeholder="—"
            value={form.stageId}
            onChange={(e) => setForm({ ...form, stageId: e.target.value })}
            options={(stages.data?.stages ?? []).map((st) => ({
              value: st.id,
              label: `${st.position}. ${st.label}`,
            }))}
          />
        )}
        <div className="row" style={{ gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <Input
            label={t('rbac.user.validFrom')}
            hint={t('rbac.user.optional')}
            type="date"
            value={form.validFrom}
            error={errors.validFrom}
            onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
          />
          <Input
            label={t('rbac.user.validUntil')}
            hint={t('rbac.user.optional')}
            type="date"
            min={form.validFrom || undefined}
            value={form.validUntil}
            error={errors.validUntil}
            onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
          />
        </div>
        {sod && (
          <Alert tone="danger" title={t('rbac.user.sodTitle')}>
            <ul style={{ margin: '0 0 var(--space-2)', paddingInlineStart: '1.2em' }}>
              {(sod.rules ?? []).map((r) => (
                <li key={r.code}>{r.message}</li>
              ))}
            </ul>
            {sod.forceable ? (
              <label className="check-row">
                <input
                  type="checkbox"
                  checked={force}
                  onChange={(e) => setForce(e.target.checked)}
                />
                <span>{t('rbac.user.sodForce')}</span>
              </label>
            ) : (
              <span>{t('rbac.user.sodNoForce')}</span>
            )}
          </Alert>
        )}
        <Textarea
          label={t('rbac.user.reason')}
          required
          rows={3}
          hint="10 caractères minimum. Le motif est conservé dans l’historique."
          value={form.reason}
          error={errors.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
        />
      </form>
    </Modal>
  )
}
